import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PermissionChip } from './index';
import type { PilotFilters, PilotType, Permission } from '../../types';
import { useI18n } from '../../i18n';

const PERMISSION_OPTIONS: Permission[] = ['BF2', 'BF3', 'BF4', 'HIPO'];

interface PilotFilterSidebarProps {
  filters: PilotFilters;
  onFiltersChange: (filters: Partial<PilotFilters>) => void;
  onClose?: () => void;
}

export function PilotFilterSidebar({ filters, onFiltersChange, onClose }: PilotFilterSidebarProps) {
  const { t } = useI18n();
  const pilotTypeOptions: { value: PilotType; label: string }[] = [
    { value: 'WORKER', label: t('common.worker') },
    { value: 'B2B', label: 'B2B' },
    { value: 'QPRO_USER', label: 'QPro User' },
  ];

  const handlePermissionToggle = (permission: Permission) => {
    const currentPermissions = filters.permissions ?? [];
    const updated = currentPermissions.includes(permission)
      ? currentPermissions.filter((p) => p !== permission)
      : [...currentPermissions, permission];
    onFiltersChange({ permissions: updated });
  };

  return (
    <Box sx={{ p: 2.5 }}>
      {onClose && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('filters.title')}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      )}

      <FormControl fullWidth sx={{ mb: 2.5 }}>
        <FormLabel sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>{t('filters.pilotType')}</FormLabel>
        <Select
          size="small"
          value={filters.pilotType ?? ''}
          onChange={(event) =>
            onFiltersChange({ pilotType: (event.target.value as PilotType) || undefined })
          }
          displayEmpty
        >
          <MenuItem value="">{t('filters.allTypes')}</MenuItem>
          {pilotTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl component="fieldset" sx={{ mb: 2.5 }}>
        <FormLabel sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>{t('filters.permissions')}</FormLabel>
        <FormGroup>
          {PERMISSION_OPTIONS.map((permission) => (
            <FormControlLabel
              key={permission}
              control={
                <Checkbox
                  size="small"
                  checked={filters.permissions?.includes(permission) ?? false}
                  onChange={() => handlePermissionToggle(permission)}
                />
              }
              label={<PermissionChip permission={permission} />}
            />
          ))}
        </FormGroup>
      </FormControl>

      <Button
        variant="outlined"
        fullWidth
        size="small"
        onClick={() => onFiltersChange({ pilotType: undefined, permissions: [] })}
      >{t('filters.clear')}</Button>
    </Box>
  );
}
